import { useCallback, useEffect, useRef, useState } from 'react'
import { Bot, MessageCircle, Paperclip, Power, RefreshCw, Send, Smartphone, Unplug, UserRound } from 'lucide-react'
import { Papel } from '@du/shared'
import {
  Badge,
  Button,
  EmptyState,
  Textarea,
  Icone,
  Input,
  Modal,
  SectionTitle,
  Skeleton,
  StatTile,
  useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { useSessao } from '../lib/sessao.js'
import type { EventoSse } from '../lib/tempoReal.js'

type Status = 'NAO_CONFIGURADO' | 'STOPPED' | 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'FAILED'

type Sessao = {
  status: Status
  telefone: string | null
  nome: string | null
  provedor?: string
}

type Mensagem = {
  id: string
  direcao: string
  texto: string | null
  tipo: string
  midiaUrl: string | null
  statusEntrega: string | null
  criadoEm: string
}

type ConversaAberta = {
  id: string
  telefone: string
  nome: string | null
  status: string
  mensagens: Mensagem[]
}

type Conversa = {
  id: string
  telefone: string
  nome: string | null
  status: string
  ultimaMsgEm: string | null
  ultima: { texto: string | null; direcao: string; criadoEm: string } | null
}

const ROTULO: Record<Status, string> = {
  NAO_CONFIGURADO: 'não configurado',
  STOPPED: 'desligado',
  STARTING: 'iniciando',
  SCAN_QR_CODE: 'aguardando leitura',
  WORKING: 'conectado',
  FAILED: 'com falha',
}


/**
 * O QR do WhatsApp expira em ~20 segundos e é trocado por outro. Buscar de
 * novo não é polling de dado — é a única forma de ter um QR válido na tela.
 * Para a mudança de ESTADO (pareou, caiu) não há busca nenhuma: ela chega
 * pelo SSE, publicado pelo webhook do WAHA.
 */
const RENOVAR_QR_MS = 20_000

/**
 * O que mostrar no lugar do identificador.
 *
 * O WhatsApp passou a identificar por LID — um número de 14 dígitos que NÃO é
 * telefone. Mostrar "56414462546084" na tela do balcão é pior que mostrar
 * nada: parece um telefone errado, e alguém vai tentar ligar.
 */
function rotular(c: { nome?: string | null; telefone: string }): string {
  if (c.nome) return c.nome
  const digitos = c.telefone.replace(/\D/g, '')
  // O mascarado da lista vem com parêntese; sem ele e com mais de 13 dígitos,
  // é LID, não telefone.
  if (!c.telefone.includes('(') && !c.telefone.includes('•') && digitos.length > 13) {
    return 'Cliente sem número'
  }
  return c.telefone
}

export function Whatsapp({ registrar }: { registrar: (fn: (e: EventoSse) => void) => void }) {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [sessao, setSessao] = useState<Sessao | null>(null)
  const [conversas, setConversas] = useState<Conversa[]>([])
  const [qr, setQr] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [confirmarSaida, setConfirmarSaida] = useState(false)
  const [telefoneTeste, setTelefoneTeste] = useState('')
  const [conversa, setConversa] = useState<ConversaAberta | null>(null)
  const [rascunho, setRascunho] = useState('')

  const gestor = usuario?.papel === Papel.ADMIN || usuario?.papel === Papel.GERENTE

  const carregar = useCallback(async () => {
    try {
      const s = await api<Sessao>('/v1/whatsapp/sessao')
      setSessao(s)
      if (s.status === 'WORKING') {
        setConversas(await api<Conversa[]>('/v1/whatsapp/conversas'))
      }
    } catch (e) {
      // Um erro aqui é quase sempre "o container do WAHA não subiu". A tela
      // precisa dizer isso em vez de ficar girando para sempre.
      setSessao({ status: 'FAILED', telefone: null, nome: null })
      avisar({
        tom: 'erro',
        texto: 'Não consegui falar com o WhatsApp',
        detalhe: e instanceof ErroApi ? e.message : undefined,
      })
    }
  }, [avisar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // O webhook do WAHA publica whatsapp.sessao: a tela vira "conectado" no
  // instante em que o Dú termina de escanear, sem ninguém apertar nada.
  const abrir = useCallback(
    async (id: string) => {
      try {
        setConversa(await api<ConversaAberta>(`/v1/whatsapp/conversas/${id}`))
      } catch (e) {
        avisar({ tom: 'erro', texto: 'Não consegui abrir', detalhe: e instanceof ErroApi ? e.message : undefined })
      }
    },
    [avisar],
  )

  // Guardo o id aberto num ref para o ouvinte do SSE não ser recriado a cada
  // mensagem nova — recriar faz o fluxo reassinar e perder eventos.
  const abertaRef = useRef<string | null>(null)
  abertaRef.current = conversa?.id ?? null

  useEffect(() => {
    registrar((e) => {
      if (e.tipo === 'whatsapp.sessao' || e.tipo === 'whatsapp.mensagem') {
        void carregar()
        const aberta = abertaRef.current
        if (aberta && (e.conversaId === aberta || e.conversaId === undefined)) void abrir(aberta)
      }
    })
  }, [registrar, carregar, abrir])

  const buscarQr = useCallback(async () => {
    try {
      const { imagem } = await api<{ imagem: string }>('/v1/whatsapp/sessao/qr')
      setQr(imagem)
    } catch {
      // QR ainda não pronto é o caso normal no primeiro segundo após iniciar.
      // Silêncio aqui: o próximo ciclo pega.
      setQr(null)
    }
  }, [])

  const aguardandoQr = sessao?.status === 'SCAN_QR_CODE'
  const timerQr = useRef<number | null>(null)

  useEffect(() => {
    if (!aguardandoQr) {
      setQr(null)
      return
    }
    void buscarQr()
    timerQr.current = window.setInterval(() => void buscarQr(), RENOVAR_QR_MS)
    return () => {
      if (timerQr.current !== null) window.clearInterval(timerQr.current)
    }
  }, [aguardandoQr, buscarQr])

  const acao = async (caminho: string, oQueFalhou: string) => {
    setOcupado(true)
    try {
      setSessao(await api<Sessao>(caminho, { method: 'POST' }))
    } catch (e) {
      avisar({
        tom: 'erro',
        texto: oQueFalhou,
        detalhe: e instanceof ErroApi ? e.message : undefined,
      })
    } finally {
      setOcupado(false)
    }
  }

  if (!sessao) return <div className="pagina-gestao"><Skeleton altura={280} /></div>

  if (sessao.status === 'NAO_CONFIGURADO') {
    return (
      <div className="pagina-gestao">
        <section className="bloco-gestao">
          <SectionTitle variante="laranja" tamanho="sm">WhatsApp</SectionTitle>
          <EmptyState
            icone={MessageCircle}
            titulo="WhatsApp ainda não configurado"
            texto="Falta a chave do serviço no servidor. Enquanto isso, o atendimento segue no aparelho do balcão normalmente."
          />
          <p className="nota-alerta">
            No servidor: ponha <code>WHATSAPP_ATIVO=true</code> no arquivo <code>.env</code> e
            suba o container com o perfil <code>waha</code>. Depois é só ler o QR aqui nesta tela.
          </p>
        </section>
      </div>
    )
  }

  return (
    <div className="pagina-gestao">
      <section className="bloco-gestao">
        <SectionTitle
          variante="laranja"
          tamanho="sm"
          acao={
            <Button variante="ghost" tamanho="sm" icone={RefreshCw} onClick={() => void carregar()}>
              Atualizar
            </Button>
          }
        >
          WhatsApp do Dú
        </SectionTitle>

        <div className="grade-stats">
          <StatTile
            rotulo="Conexão"
            valor={ROTULO[sessao.status]}
            tom={sessao.status === 'WORKING' ? 'marca' : 'destaque'}
          />
          <StatTile rotulo="Número" valor={sessao.telefone ? formatar(sessao.telefone) : '—'} />
          <StatTile rotulo="Conversas" valor={String(conversas.length)} apoio="últimas 20" />
        </div>

        {sessao.status === 'STOPPED' || sessao.status === 'FAILED' ? (
          <>
            {sessao.status === 'FAILED' ? (
              <p className="nota-alerta nota-alerta--erro">
                O código expirou sem ser lido. O WhatsApp só emite alguns QR por sessão — dá
                cerca de <strong>2 minutos</strong> no total. Clique em conectar com o celular
                da loja já na mão, destravado e no WhatsApp.
              </p>
            ) : null}
            <div className="linha-acoes">
              <Button
                icone={Power}
                disabled={!gestor || ocupado}
                onClick={() => void acao('/v1/whatsapp/sessao/iniciar', 'Não consegui iniciar')}
              >
                Conectar WhatsApp
              </Button>
            </div>
          </>
        ) : null}

        {sessao.status === 'STARTING' ? (
          <p className="nota-alerta">Iniciando o serviço. O QR aparece em alguns segundos.</p>
        ) : null}

        {aguardandoQr ? (
          <div className="pareamento">
            <div className="pareamento__qr">
              {qr ? (
                /* alt vazio e aria-hidden: o QR não tem leitura útil para quem
                   usa leitor de tela — o passo a passo ao lado é que serve. */
                <img src={qr} alt="" aria-hidden width={264} height={264} />
              ) : (
                <Skeleton altura={264} />
              )}
            </div>
            <div className="pareamento__passos">
              <strong>Leia com o celular do Dú</strong>
              <ol>
                <li>Deixe o celular da loja destravado e no WhatsApp <b>antes</b> de começar</li>
                <li>Abra o WhatsApp no celular do número da loja</li>
                <li>Toque nos três pontinhos e depois em <b>Dispositivos conectados</b></li>
                <li>Toque em <b>Conectar dispositivo</b></li>
                <li>Aponte a câmera para este código</li>
              </ol>
              <p className="pareamento__aviso">
                O código troca sozinho a cada 20 segundos, e são poucos: você tem cerca de
                2 minutos no total. Se passar disso, é só clicar em conectar de novo.
              </p>
              <Button
                variante="ghost"
                tamanho="sm"
                disabled={ocupado}
                onClick={() => void acao('/v1/whatsapp/sessao/parar', 'Não consegui parar')}
              >
                Cancelar
              </Button>
            </div>
          </div>
        ) : null}

        {sessao.status === 'WORKING' ? (
          <>
            <p className="nota-alerta">
              Conectado como <strong>{sessao.nome ?? 'WhatsApp da loja'}</strong>
              {sessao.telefone ? ` · ${formatar(sessao.telefone)}` : ''}.
              Confira se é mesmo o número da loja antes de deixar rodando.
            </p>
            <div className="form-linha">
              <Input
                rotulo="Enviar teste para"
                placeholder="(11) 99999-9999"
                inputMode="tel"
                value={telefoneTeste}
                onChange={(e) => setTelefoneTeste(e.target.value)}
              />
              <Button
                icone={Send}
                disabled={!gestor || ocupado || telefoneTeste.replace(/\D/g, '').length < 10}
                onClick={async () => {
                  setOcupado(true)
                  try {
                    await api('/v1/whatsapp/teste', {
                      method: 'POST',
                      corpo: { telefone: telefoneTeste },
                    })
                    avisar({ tom: 'sucesso', texto: 'Mensagem enviada. Confira o aparelho.' })
                  } catch (e) {
                    avisar({
                      tom: 'erro',
                      texto: 'Não consegui enviar',
                      detalhe: e instanceof ErroApi ? e.message : undefined,
                    })
                  } finally {
                    setOcupado(false)
                  }
                }}
              >
                Enviar
              </Button>
            </div>
            <div className="linha-acoes">
              <Button
                variante="ghost"
                icone={Unplug}
                disabled={!gestor || ocupado}
                onClick={() => setConfirmarSaida(true)}
              >
                Desconectar número
              </Button>
            </div>
          </>
        ) : null}
      </section>

      {sessao.status === 'WORKING' ? (
        <section className="bloco-gestao">
          <SectionTitle variante="vazado" tamanho="sm">Últimas conversas</SectionTitle>
          {conversas.length === 0 ? (
            <EmptyState
              icone={Smartphone}
              titulo="Nenhuma conversa ainda"
              texto="Assim que alguém mandar mensagem para a loja, aparece aqui."
            />
          ) : (
            <ul className="lista-simples">
              {conversas.map((c) => (
                <li key={c.id}>
                  <span>
                    <Icone de={MessageCircle} tamanho={16} />
                    {rotular(c)}
                    <Badge tom={c.status === 'HUMANO' ? 'alerta' : 'neutro'}>{c.status}</Badge>
                  </span>
                  <span className="lista-simples__fraco">
                    {c.ultimaMsgEm
                      ? new Date(c.ultimaMsgEm).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''}
                  </span>
                  {c.ultima?.texto ? (
                    <span className="lista-simples__fraco conversa__previa">
                      {c.ultima.direcao === 'SAIDA' ? 'Você: ' : ''}
                      {c.ultima.texto.slice(0, 90)}
                    </span>
                  ) : null}
                  <span className="lista-simples__acoes">
                    <Button variante="ghost" tamanho="sm" onClick={() => void abrir(c.id)}>
                      Abrir
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      <Modal
        aberto={conversa !== null}
        aoFechar={() => { setConversa(null); setRascunho('') }}
        titulo={conversa ? rotular(conversa) : 'Conversa'}
      >
        {conversa ? (
          <div className="form-modal">
            <p className="lista-simples__fraco">
              <Icone de={conversa.status === 'BOT' ? Bot : UserRound} tamanho={14} />
              {conversa.status === 'BOT'
                ? ' A IA está atendendo. Se você responder, ela cala e você assume.'
                : ' Você assumiu esta conversa. A IA não responde aqui.'}
            </p>

            <div className="chat">
              {conversa.mensagens.length === 0 ? (
                <p className="nota-alerta">Nenhuma mensagem ainda.</p>
              ) : (
                conversa.mensagens.map((m) => (
                  <div
                    key={m.id}
                    className={`chat__balao chat__balao--${m.direcao === 'SAIDA' ? 'nosso' : 'deles'}`}
                  >
                    {m.midiaUrl && m.tipo === 'image' ? (
                      <img src={m.midiaUrl} alt={m.texto ?? 'imagem'} className="chat__midia" />
                    ) : m.midiaUrl && (m.tipo === 'audio' || m.tipo === 'ptt') ? (
                      // controls, não autoplay: áudio tocando sozinho na
                      // cozinha com o barulho da chapa não serve a ninguém.
                      <audio src={m.midiaUrl} controls preload="none" className="chat__midia" />
                    ) : m.midiaUrl && m.tipo === 'video' ? (
                      <video src={m.midiaUrl} controls preload="none" className="chat__midia" />
                    ) : m.midiaUrl ? (
                      <a href={m.midiaUrl} target="_blank" rel="noreferrer">
                        {m.texto ?? 'Abrir arquivo'}
                      </a>
                    ) : (
                      (m.texto ?? `[${m.tipo}]`)
                    )}
                    <time>
                      {new Date(m.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {m.direcao === 'SAIDA' && m.statusEntrega ? ` · ${m.statusEntrega.toLowerCase()}` : ''}
                    </time>
                  </div>
                ))
              )}
            </div>

            <Textarea
              rotulo="Responder"
              rows={2}
              placeholder="Escreva aqui…"
              value={rascunho}
              onChange={(e) => setRascunho(e.target.value)}
            />
            <div className="linha-acoes">
              <label className="du-botao du-botao--ghost du-botao--md anexo">
                <Icone de={Paperclip} tamanho={18} />
                <span className="du-botao__rotulo">Anexar</span>
                <input
                  type="file"
                  accept="image/*,video/*,audio/*,application/pdf"
                  disabled={!gestor || ocupado}
                  onChange={async (ev) => {
                    const f = ev.target.files?.[0]
                    ev.target.value = ''
                    if (!f) return
                    // 1 MB é o limite de corpo da API; base64 ainda cresce o
                    // arquivo em ~33%, então o teto real é menor.
                    if (f.size > 700_000) {
                      avisar({ tom: 'erro', texto: 'Arquivo grande demais', detalhe: 'Máximo 700 KB por enquanto.' })
                      return
                    }
                    setOcupado(true)
                    try {
                      const base64 = await new Promise<string>((ok, falha) => {
                        const r = new FileReader()
                        // readAsDataURL devolve "data:tipo;base64,XXXX" — o
                        // WAHA quer só o XXXX.
                        r.onload = () => ok(String(r.result).split(',')[1] ?? '')
                        r.onerror = () => falha(new Error('leitura falhou'))
                        r.readAsDataURL(f)
                      })
                      await api(`/v1/whatsapp/conversas/${conversa.id}/arquivo`, {
                        method: 'POST',
                        corpo: {
                          mimetype: f.type || 'application/octet-stream',
                          filename: f.name,
                          base64,
                          ...(rascunho.trim() ? { legenda: rascunho.trim() } : {}),
                        },
                      })
                      setRascunho('')
                      await abrir(conversa.id)
                      await carregar()
                    } catch (e) {
                      avisar({ tom: 'erro', texto: 'Não consegui enviar', detalhe: e instanceof ErroApi ? e.message : undefined })
                    } finally {
                      setOcupado(false)
                    }
                  }}
                />
              </label>
              <Button
                icone={Send}
                disabled={!gestor || ocupado || rascunho.trim().length === 0}
                onClick={async () => {
                  setOcupado(true)
                  try {
                    await api(`/v1/whatsapp/conversas/${conversa.id}/responder`, {
                      method: 'POST',
                      corpo: { texto: rascunho.trim() },
                    })
                    setRascunho('')
                    await abrir(conversa.id)
                    await carregar()
                  } catch (e) {
                    avisar({ tom: 'erro', texto: 'Não consegui enviar', detalhe: e instanceof ErroApi ? e.message : undefined })
                  } finally {
                    setOcupado(false)
                  }
                }}
              >
                Enviar
              </Button>
              {conversa.status === 'HUMANO' ? (
                <Button
                  variante="ghost"
                  icone={Bot}
                  disabled={!gestor || ocupado}
                  onClick={async () => {
                    await api(`/v1/whatsapp/conversas/${conversa.id}/devolver`, { method: 'POST' })
                    await abrir(conversa.id)
                    await carregar()
                  }}
                >
                  Devolver para a IA
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        aberto={confirmarSaida}
        aoFechar={() => setConfirmarSaida(false)}
        titulo="Desconectar o número?"
        rodape={
          <>
            <Button variante="ghost" onClick={() => setConfirmarSaida(false)}>
              Deixar conectado
            </Button>
            <Button
              disabled={ocupado}
              onClick={async () => {
                setConfirmarSaida(false)
                await acao('/v1/whatsapp/sessao/desconectar', 'Não consegui desconectar')
              }}
            >
              Desconectar
            </Button>
          </>
        }
      >
        <p>
          O número sai de <strong>Dispositivos conectados</strong> no celular do Dú. O sistema
          para de enviar e de receber mensagens na hora.
        </p>
        <p className="nota-alerta">
          Para voltar, alguém precisa escanear o QR de novo com o celular da loja em mãos.
        </p>
      </Modal>
    </div>
  )
}

/** 5511999999999 -> (11) 99999-9999. Sem o 55, que não interessa a ninguém aqui. */
function formatar(digitos: string): string {
  const d = digitos.startsWith('55') ? digitos.slice(2) : digitos
  if (d.length < 10) return digitos
  const ddd = d.slice(0, 2)
  const resto = d.slice(2)
  const corte = resto.length - 4
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`
}
